import useBookingStore from "@/store/booking-store";
import { Radio, RadioGroup } from "@heroui/react";
import { useLocale, useTranslations } from "../../../../../i18n";
import { useStudioStore } from "@/store/studio-store";

// Helper function to get localized text
const getLocalizedText = (
  multilingualData: any,
  locale: string,
  fallback: string | null,
  defaultLocale: string = "en",
): string => {
  // If no multilingual data, return fallback
  if (!multilingualData || typeof multilingualData !== "object") {
    return fallback || "";
  }

  // Try to get text in current locale
  if (multilingualData[locale]) {
    return multilingualData[locale];
  }

  // Try default locale
  if (multilingualData[defaultLocale]) {
    return multilingualData[defaultLocale];
  }

  // Try first available language
  const availableLanguages = Object.keys(multilingualData);
  if (
    availableLanguages.length > 0 &&
    multilingualData[availableLanguages[0]]
  ) {
    return multilingualData[availableLanguages[0]];
  }

  // Final fallback
  return fallback || "";
};

function BookingOptionsContentTab({
  items = [],
  onSelect,
}: {
  items?: any[];
  onSelect?: () => void;
}) {
  const locale = useLocale();
  const studio = useStudioStore((state) => state.studio);
  const t = useTranslations("checkout");
  const {
    selectedPassId,
    setSelectedPassId,
    setSelectedPass,
    setSelectedUserPass,
    setBookingMethod,
  } = useBookingStore();

  const handlePassSelection = (pass: any) => {
    setBookingMethod("NEW_PASS");
    setSelectedPassId(pass.id);
    setSelectedPass(pass);
    setSelectedUserPass(null);
    onSelect?.();
  };

  // Helper function to get localized pass name
  const getLocalizedPassName = (pass: any): string => {
    if (!pass) return "";
    if (typeof pass.title === "string") return pass.title;
    return getLocalizedText(
      pass.title,
      locale,
      pass.name,
      studio?.defaultLocale || "en",
    );
  };

  // Helper function to get localized pass description
  const getLocalizedPassDescription = (pass: any): string => {
    return getLocalizedText(
      pass.descriptions,
      locale,
      pass.description,
      studio?.defaultLocale || "en",
    );
  };

  // Format classes display
  const formatClasses = (classes: number | null): string => {
    if (classes === null) return t("unlimited") || "Unlimited";
    return classes === 1
      ? t("oneClass") || "1 class"
      : `${classes} ${t("classes")}`;
  };

  // Determine if we need to show tabs based on content
  const hasItems = items.length > 0;

  const getSelectedValue = () => {
    if (selectedPassId) return selectedPassId;
    return "dropIn";
  };

  // If we have both passes and pricing plans, show both
  if (hasItems) {
    return (
      <RadioGroup
        value={getSelectedValue()}
        onValueChange={(id) => {
          const pass = items.find((p) => p.id === id);
          if (pass) {
            handlePassSelection(pass);
            return;
          }
        }}
        className="w-full"
      >
        {/* Display Passes */}
        {items
          .sort((a, b) => a.price - b.price)
          .map((pass) => {
            const localizedName = getLocalizedPassName(pass);
            const localizedDescription = getLocalizedPassDescription(pass);

            return (
              <div
                key={pass.id}
                className="flex justify-between items-center p-4 border rounded-lg mb-2 cursor-pointer hover:bg-gray-50"
                onClick={() => handlePassSelection(pass)}
              >
                <Radio value={pass.id}>
                  <div className="flex items-center ml-4">
                    <div className="flex flex-col gap-1">
                      <span className="font-medium">{localizedName}</span>
                      <span className="text-sm text-gray-400">
                        {localizedDescription}
                      </span>
                    </div>
                  </div>
                </Radio>

                <div className="text-right">
                  <span className="font-medium">
                    {pass.price.toFixed(2)} {studio?.currency || "CHF"}
                  </span>
                  <div className="text-sm text-gray-500">
                    {formatClasses(pass.classes)}
                  </div>
                </div>
              </div>
            );
          })}
      </RadioGroup>
    );
  }

  return (
    <div className="text-gray-500">
      {t("noBookingOptions") ||
        "No booking options available for this category."}
    </div>
  );
}

export default BookingOptionsContentTab;
